import React, {
  useContext,
  useState,
  ChangeEvent,
  useEffect,
} from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import MultiLineComment from '../../../components/Common/MultiLineComment';

import NotificationProvider from '../../../providers/Notification/NotificationProvider';

import {
  CustomerType,
  PricingSheet,
  PricingSheetState,
} from '../../../../../shared/types';

import {
  AdditionalInfo,
  updatePricingSheetById,
} from '../../../services/pricingSheets';

import CustomerSelector from './CustomerSelector';
import {
  ClientIdType,
  createOption,
  getClientIdValues,
} from './CustomerSelector';

import { getUniques } from '../../../utils/getUniques';
import { getCustomerTypeLabel } from '../../../helpers/getCustomerTypeLabel';
import { useQuery } from '@tanstack/react-query';
import { CUSTOMERS_BY_COMPANYCHAIN_KEY } from '../../../../../shared/constants';
import { getCustomersByCompanyChainId } from '../../../services/customers';
import { Alert, CircularProgress, Typography } from '@mui/material';
import CustomerSearchSelector from '../../../components/Steps/Components/Selectors/CustomerSearchSelector';

type MiraDialogFormState = Omit<AdditionalInfo, 'clientIds'>;
export interface MiraDialogProps {
  pricingSheet: PricingSheet;
  open: boolean;
  title: string;
  updateSheets: () => void;
  onClose: () => void;
}
const MiraDialog = (props: MiraDialogProps) => {
  const { pricingSheet, open, updateSheets, title, onClose } = props;

  const { data: customers, isLoading } = useQuery({
    queryKey: [CUSTOMERS_BY_COMPANYCHAIN_KEY, pricingSheet.companyId],
    queryFn: () =>
      getCustomersByCompanyChainId(pricingSheet.companyId ?? ''),
    enabled: Boolean(pricingSheet.companyId) && open,
  });

  const isProspect =
    pricingSheet.customerType === CustomerType.PROSPECT;

  const { setNotification } = useContext(NotificationProvider);
  const pricingSheetMiraInfo = pricingSheet?.miraInfo;

  const defaultClientIds = pricingSheetMiraInfo
    ? pricingSheetMiraInfo.customerIds?.map((id) => createOption(id))
    : [];

  const defaultClientIdsFromInitialStep =
    pricingSheet?.customerId && !isProspect
      ? [createOption(pricingSheet?.customerId)]
      : [];

  const [clientIds, setClientIds] = useState<readonly ClientIdType[]>(
    defaultClientIds?.length > 0
      ? defaultClientIds
      : defaultClientIdsFromInitialStep,
  );

  const initialFormState = {
    oldSheetId: pricingSheetMiraInfo?.miraId || '',
    projectId: '',
    comment: '',
    clientIds: [],
  };

  const isExportDisabled = clientIds.length === 0;

  const [formState, setFormState] =
    useState<MiraDialogFormState>(initialFormState);

  const handleInputChange =
    (name: keyof MiraDialogFormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setFormState({ ...formState, [name]: value });
    };

  const sendExportRequest = async () => {
    if (clientIds.length === 0) {
      setNotification({
        type: 'SNACKBAR',
        duration: 2000,
        severity: 'error',
        message: 'Lisää asiakasnumero(t) ja vanhan hinnaston id!',
      });
    } else {
      const additionalInfo: AdditionalInfo = {
        oldSheetId: formState.oldSheetId || undefined,
        projectId: formState.projectId || undefined,
        comment: formState.comment || undefined,
        clientIds: getUniques(getClientIdValues(clientIds)),
      };

      try {
        await updatePricingSheetById(
          pricingSheet.id,
          {
            state: PricingSheetState.Ready,
          },
          additionalInfo,
        );
        updateSheets();
        setNotification({
          type: 'SNACKBAR',
          duration: 2000,
          severity: 'success',
          message: 'Vientipyyntö hinnastotiimille lähetetty!',
        });
      } catch (error) {
        console.error(error);
        setNotification({
          type: 'SNACKBAR',
          duration: 2000,
          severity: 'error',
          message: 'Vientipyyntöä lähettäessä tapahtui virhe!',
        });
      }
      clearAndClose();
    }
  };

  const clearAndClose = () => {
    setFormState(initialFormState);
    onClose();
  };

  useEffect(() => {
    if (customers && customers.length > 0 && open) {
      setClientIds(
        customers.map((customer: any) => {
          return {
            label: `${customer.clientName}, ${customer.clientId}`,
            value: customer.clientId,
          };
        }),
      );
    }
  }, [customers, open]);

  return (
    <Dialog
      disableScrollLock
      fullWidth
      open={open}
      onClose={clearAndClose}
    >
      <DialogTitle id="mira-dialog-title">{title}</DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <DialogContentText color={'text.primary'}>
            {pricingSheet.name}
          </DialogContentText>

          <DialogContentText color={'text.secondary'}>
            {`${pricingSheet.customerName}, ${pricingSheet?.companyId ?? ''}  ${pricingSheet?.customerId ?? ''}`}
          </DialogContentText>
          <DialogContentText color={'text.secondary'}>
            {pricingSheet.customerType === CustomerType.COMPANY_CHAIN
              ? getCustomerTypeLabel(pricingSheet.customerType)
              : ''}
          </DialogContentText>
        </Box>
        {
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {isProspect && (
                <>
                  <Alert severity="warning">
                    <Typography>
                      Hinnasto on tehty prospektille. Hinnastolle
                      täytyy lisätä asiakasnumero ennen kuin
                      vientipyynnön voi tehdä
                    </Typography>
                  </Alert>
                  <CustomerSearchSelector
                    showCustomerCard={false}
                    disableProspects={true}
                    customerName={''}
                    customerId={''}
                    companyId={''}
                    onCustomerChange={(clientOption) => {
                      if (clientOption) {
                        setClientIds(
                          clientIds.concat({
                            label: `${clientOption.value}, ${clientOption.id}`,
                            value: clientOption.id ?? '',
                          }),
                        );
                      }
                    }}
                  />
                </>
              )}
              <DialogContentText>
                Asiakasnumerot, joihin hinnasto liitetään
              </DialogContentText>
              {isLoading ? (
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'start',
                    px: 2,
                  }}
                >
                  <CircularProgress size={18} thickness={5} />{' '}
                  Ladataan asiakasnumeroita...
                </Box>
              ) : (
                <CustomerSelector
                  disabled={false}
                  clientIds={clientIds}
                  setClientIds={setClientIds}
                />
              )}
              <DialogContentText>
                Päivitettävän hinnaston Id (numero)
              </DialogContentText>
              <TextField
                value={formState.oldSheetId}
                onChange={handleInputChange('oldSheetId')}
                id="outlined-basic"
                label="Hinnaston ID"
                variant="outlined"
                size="small"
                type="number"
                InputProps={{ inputProps: { min: 1 } }}
              />
              <DialogContentText>
                Projektinumero, mikäli projektihinnasto
              </DialogContentText>
              <TextField
                value={formState.projectId}
                onChange={handleInputChange('projectId')}
                id="outlined-basic"
                label="Projektinumero"
                variant="outlined"
                size="small"
                InputProps={{ inputProps: { min: 1 } }}
              />
              <DialogContentText>
                Kommentti hinnastotiimille
              </DialogContentText>
              <MultiLineComment
                label="Esim. hinnastoketjutus"
                placeholder="Lisää kommentti"
                handleCommentChange={handleInputChange('comment')}
                required={false}
              />
            </Box>

            <DialogActions sx={{ mt: 2 }}>
              <Button onClick={clearAndClose}>Peru</Button>
              <Button
                variant="contained"
                onClick={sendExportRequest}
                autoFocus
                disabled={isExportDisabled}
              >
                Lähetä vientipyyntö
              </Button>
            </DialogActions>
          </>
        }
      </DialogContent>
    </Dialog>
  );
};

export default MiraDialog;
